import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import {
  Activity, CheckCircle2, XCircle, Clock, Mail, Zap, Database,
  RefreshCw, AlertCircle, Hash, Briefcase, FileText, Download, Lock,
  Plug, Link2, Copy, ExternalLink, Sparkles, TrendingUp, ArrowRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { OUTREACH_TEMPLATES } from "@/data/outreachTemplates";

const fmtRel = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff/60)}m ago`;
  if (diff < 86400) return `${Math.round(diff/3600)}h ago`;
  return `${Math.round(diff/86400)}d ago`;
};

export default function AdminSystem() {
  const [auth, setAuth] = useState({ checked: false, allowed: false });
  const [scheduler, setScheduler] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [events, setEvents] = useState(null);
  const [rateLimits, setRateLimits] = useState(null);
  const [briefings, setBriefings] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/admin/me")
      .then(r => setAuth({ checked: true, allowed: r.data.is_super_admin }))
      .catch(() => setAuth({ checked: true, allowed: false }));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const [s, i, e, r, b] = await Promise.all([
        api.get("/admin/system/scheduler").then(x => x.data),
        api.get("/admin/system/integrations").then(x => x.data),
        api.get("/admin/system/events").then(x => x.data),
        api.get("/admin/system/rate-limits").then(x => x.data),
        api.get("/admin/system/briefings").then(x => x.data),
      ]);
      setScheduler(s); setIntegrations(i); setEvents(e); setRateLimits(r); setBriefings(b);
    } catch (_) { /* swallow */ }
    setRefreshing(false);
  };

  useEffect(() => {
    if (!auth.allowed) return;
    refresh();
    const t = setInterval(refresh, 30_000); // auto-refresh every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.allowed]);

  if (!auth.checked) return <div className="p-10 font-mono text-[11px] text-zinc-500">CHECKING…</div>;
  if (!auth.allowed) return (
    <div className="p-10" data-testid="system-denied">
      <h1 className="font-heading font-black text-3xl uppercase tracking-tighter">403 — Super-admin only.</h1>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="admin-system">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MODULE · SYSTEM</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter uppercase mt-1 flex items-center gap-3">
            <Activity className="w-7 h-7 text-[#FFB000]" /> Observability
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Live cron jobs, integrations, recent events, and per-user usage. Auto-refreshes every 30s.</p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          data-testid="system-refresh-btn"
          className="border border-zinc-700 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-zinc-900 hover:text-[#FFB000] flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* ── Bespoke Proposals · super-admin index of all 1-to-1 outreach landing pages ── */}
      <BespokeProposalsPanel />

      {/* ── Weekly leads funnel tile (Phase 25.16x) ── */}
      <WeeklyLeadsFunnelTile />

      {/* ── Exploration Trial · 7-day signals tile (Phase 25.16ay-deepen) ── */}
      <ExplorationTrialSignalsTile />

      {/* ── Homepage signal-strip editor (Phase 25.16r) ── */}
      <HomepageTickerPanel />

      {/* ── Private Internal Briefs · super-admin only, NEVER linked publicly ── */}
      <Panel title="// PRIVATE BRIEFS · NOT FOR PUBLIC RELEASE" testId="private-briefs-panel" meta={<Lock className="w-3.5 h-3.5 text-[#FFB000]" />}>
        <div className="p-4 sm:p-5 space-y-3">
          <p className="text-xs text-zinc-400 leading-relaxed">
            Internal strategic briefs prepared for the founder. These PDFs are gated to your
            super-admin account, never linked from any public daraos.ai page, and never indexed
            by search engines. Treat as confidential.
          </p>
          <PrivateBriefRow
            testId="brief-indigenous-grants"
            slug="indigenous-grants"
            title="Indigenous Grant Outcome Measurement Framework"
            sub="6-layer framework · 5 benchmark types · worked NIAA example · standards · references."
            pages="~10 pages"
          />
          <PrivateBriefRow
            testId="brief-dara-impact-entity"
            slug="dara-impact-entity"
            title="DARA Impact Pty Ltd — Structuring Blueprint"
            sub="Cap table · IP licensing · Supply Nation + IPP · governance · IRAP path · funding pathway · 24-month timeline."
            pages="~12 pages"
          />
          <PrivateBriefRow
            testId="brief-investor-pitch"
            slug="investor-pitch"
            title="DARA OS — Investor Pitch Companion"
            sub="Elevator pitch · market sizing (TAM/SAM/SOM) · competitor landscape · traction narrative · $2M seed ask · honest limits · pitch checklist."
            pages="~11 pages"
          />
          <PrivateBriefRow
            testId="brief-onboarding-playbook"
            slug="onboarding-playbook"
            title="DARA OS — Customer Onboarding Playbook"
            sub="30-day onboarding arc · industry variants · conversion script · 15-objection cheat sheet · expansion plays · churn saves · CS metrics · 10 email templates."
            pages="~13 pages"
          />
          <PrivateBriefRow
            testId="brief-rampersand-deck"
            slug="rampersand-deck"
            title="DARA OS — Rampersand Pre-Seed Pitch Deck (v1)"
            sub="10-slide pitch deck tuned for Rampersand (Australia's most-trusted early-stage VC · Sydney/Melbourne/SF). Ask sized at A$1M LEAD pre-seed @ A$5M post-money cap — mid-range of Rampersand's A$250K-A$2.5M cheque sweet spot. AUD-native throughout (matches their LP fund context). Closing italic anchors on Rampersand's 'first to believe, first to invest' tagline + AI-native software / doing hard things thesis. Public download endpoint: /api/public/deck/rampersand."
            pages="10 slides · A4 landscape"
          />
          <PrivateBriefRow
            testId="brief-2048-ventures-deck"
            slug="2048-ventures-deck"
            title="DARA OS — 2048 Ventures Pre-Seed Pitch Deck (v1)"
            sub="10-slide pitch deck tuned for 2048 Ventures (Alex Iskold · NYC + Boston · Vertical AI thesis). Ask sized at US$500K LEAD pre-seed @ US$3M post-money — sized to 2048's Pre-Seed Fast Track range. Closing italic references 2048's vertical-AI / systems-of-record thesis directly. Public download endpoint: /api/public/deck/2048. Re-generate via `python -m scripts.generate_2048_ventures_deck`."
            pages="10 slides · A4 landscape"
          />
          <PrivateBriefRow
            testId="brief-precursor-ventures-deck"
            slug="precursor-ventures-deck"
            title="DARA OS — Precursor Ventures Pre-Seed Pitch Deck (v1)"
            sub="Same 10-slide structure as the Hustle Fund deck — re-tuned for Precursor Ventures (Charles Hudson · SF · pre-seed sweet spot US$250K-US$500K). Ask sized at US$350K founding cheque @ US$3M post-money cap. All Hustle Fund-specific anchor language stripped; neutral company voice throughout; closing italic frames US$350K as the founding cheque on the company. ALL pricing reframed in USD for US allocator audience. Re-generate via `python -m scripts.generate_precursor_ventures_deck`."
            pages="10 slides · A4 landscape"
          />
          <PrivateBriefRow
            testId="brief-hustle-fund-deck"
            slug="hustle-fund-deck"
            title="DARA OS — Hustle Fund Pre-Seed Pitch Deck (v1)"
            sub="10-slide pre-seed pitch deck in DARA's Bloomberg-terminal aesthetic — generated natively via ReportLab so the deck LOOKS like the product. Cover · Problem · Product · Why Now · Traction · Business Model · GTM · Market (bottom-up, no TAM/SAM/SOM) · Team · Ask (A$1.2M @ A$6M post-money, Hustle Fund-led US$200K anchor). Sized for Hustle Fund's 10MB upload limit (~20KB). Re-generate via `python -m scripts.generate_hustle_fund_deck` after edits."
            pages="10 slides · A4 landscape"
          />
          <PrivateBriefRow
            testId="brief-analyst-agreement"
            slug="analyst-agreement"
            title="DARA Expert — Contractor Agreement (TEMPLATE)"
            sub="Master independent-contractor agreement for analysts joining the Expert Programme. NSW-governed. 16 clauses + 3 schedules (commission rates, conduct standards, applicable law). Requires lawyer review before use."
            pages="~14 pages"
          />
          <PrivateBriefRow
            testId="brief-nab-pilot-proposal"
            slug="nab-pilot-proposal"
            title="NAB × DARA OS — 90-Day Pilot Proposal"
            sub="Single-page proposal for a paid pilot with NAB Business Banking · 50-100 SMB merchants over 90 days · $45K AUD pilot fee · 5 measurable success metrics defined upfront (connection rate ≥70%, weekly active ≥50%, AI brief consumption ≥40%, NPS ≥40, paid-tier conversion ≥15%) · clean IP/data/non-exclusivity language · 14-day exit clause. Use this same template for CBA x15, Westpac SBG, ANZ Worldline, or Tyro — just regenerate after editing the script."
            pages="1 page"
          />
          <PrivateBriefRow
            testId="brief-billion-dollar-roadmap"
            slug="billion-dollar-roadmap"
            title="DARA OS — $0 → $1B Roadmap (Super-Admin Only)"
            sub="Stage-by-stage execution roadmap from solo-founder pre-revenue to unicorn. 7 stages (Today → First Customer → PMF → Series A → Series B → Series C → Unicorn) with per-stage valuation, ARR, customer, team, and funding targets · critical actions per stage · honest probability tree (15-22% chance of $1B in 5-7 years, 55-65% chance of some material exit >$50M) · 9 operating principles. Re-generate via `python scripts/generate_billion_dollar_roadmap.py` after every stage milestone hits."
            pages="~10 pages"
          />
          <PrivateBriefRow
            testId="brief-pre-seed-safe-pack"
            slug="pre-seed-safe-pack"
            title="DARA OS — Pre-Seed SAFE Term Sheet Pack · AUD (Confidential)"
            sub="5-page signable-draft AUD SAFE pack: (1) Key Terms summary — A$1.25M raise on A$7M post-money cap, 20% discount, MFN, pro-rata for ≥A$100k cheques. (2) Full AU-adapted SAFE template with WA governing law + Corporations Act s.708(8)/(11) sophisticated/professional investor carve-outs. (3) Cap table model — 3 Series Seed scenarios (A$10M / A$15M / A$25M post) showing founder retains 53-58%. (4) Use-of-funds — A$720k people, A$110k compliance/SOC 2, A$165k sales+marketing, A$95k infra, A$160k reserve. (5) Investor FAQ + mandatory legal disclaimer. DO NOT SEND TO INVESTORS WITHOUT WA CORPORATE LAWYER REVIEW. Re-generate via `python scripts/generate_pre_seed_term_sheet.py`."
            pages="5 pages · A4"
          />
          <PrivateBriefRow
            testId="brief-pre-seed-safe-pack-usd"
            slug="pre-seed-safe-pack-usd"
            title="DARA OS — Pre-Seed SAFE Term Sheet Pack · USD (Confidential)"
            sub="USD twin for US accredited / SV angel investors: (1) Key Terms — US$1M raise on US$7M post-money cap, 20% discount, MFN, pro-rata for ≥US$75k cheques. (2) USD-denominated SAFE with Reg D 506(b) + Reg S compliance, accredited-investor warranty, Delaware-flip contingency clause, AUD/USD conversion clause (RBA closing rate). (3) Cap table at US$15M/US$22M/US$35M Series Seed scenarios — founder retains 56-61%. (4) Use-of-funds in USD, same 12-month plan as AUD. (5) Investor FAQ specifically addressing: 'why AU not Delaware?', QSBS eligibility on Delaware flip, US tax treatment. DO NOT SEND WITHOUT US SECURITIES LAWYER + WA CORPORATE LAWYER REVIEW. Re-generate via `python scripts/generate_pre_seed_term_sheet_usd.py`."
            pages="5 pages · US Letter"
          />
          <PrivateBriefRow
            testId="brief-ventia-executive-intro"
            slug="ventia-executive-intro"
            title="DARA × Ventia — Executive Introduction (1-Page Attachment)"
            sub="Single-page A4 PDF designed to be ATTACHED to the formal introduction email to Shaun Hill (Ventia · 35K FTE · 400+ sites AU/NZ). Bloomberg-terminal styling, recipient/sender header strip, WHO / WHAT / WHY VENTIA narrative, and a 7-service-line × DARA-deployable × example-output matrix (Telco · Electric & Gas · Water · Transport · Defence · Environmental · Facility Management). Contact reflects current gmail (majisonmanuel@gmail.com), not the not-yet-active @daraos.ai. Re-generate via `python scripts/generate_ventia_executive_intro.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-build-vs-buy-brief"
            slug="build-vs-buy-brief"
            title="DARA — Build vs Buy · Australian Tier-1 Industrial AI (1-Page Pre-emption)"
            sub="Single-page A4 follow-up PDF designed to pre-empt 'we're building this internally' objections. Sent ~2-3 days AFTER the Ventia executive introduction. Contains: (1) Honest TCO + time-to-value comparison table across 7 dimensions (Year-1 burn, 36-mo TCO, time-to-brief, multi-site reconciliation, key-person risk, connector library breadth, regulator pack). (2) Green-tinted 'WHEN INTERNAL BUILD WINS' section conceding 3 scenarios (single-site/single-protocol; classified IRAP PROTECTED; org IS a software co.). (3) Amber-tinted 'WHEN BUY CLEARLY WINS' section with 4 scenarios (3+ service lines; multi-jurisdiction regulator; client demands external accountability layer; velocity gap widening). (4) Dark Hybrid close — positions DARA as the engine inside a hybrid where internal team owns deployment + customisation. Use this whenever a prospect mentions an internal AI/data project — it converts the threat into your strongest sales weapon. Re-generate via `python scripts/generate_build_vs_buy_brief.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-mnda-template"
            slug="mnda-template"
            title="DARA — Mutual NDA Template (2-Page · Send Before Any Deep Technical Disclosure)"
            sub="2-page A4 mutual non-disclosure agreement designed to be sent the moment a B2B conversation moves past public materials and into architecture, roadmap, codebase, customer pipeline, or commercial terms. 11 clauses covering: confidential information definition (broad — covers source code, integration libraries, regulatory engineering, pricing), permitted use WITH non-compete restriction (recipient cannot use DARA's CI to build competing products), need-to-know standard of care, standard exclusions, compelled disclosure notice, 24-month term + 36-month survival, return/destroy on termination, no IP transfer, no commercial obligation, 12-month non-solicitation of key personnel, equitable relief, WA governing law + WA exclusive jurisdiction, Electronic Transactions Act counterparts. Fill the [COUNTERPARTY_NAME / ABN / ADDR / SIGNATORY_NAME / TITLE / EMAIL / EFFECTIVE_DATE] placeholders before sending. IMPORTANT: this is a template; a WA corporate lawyer must review clauses 6/9/11 before formal execution at scale. Re-generate via `python scripts/generate_mnda_template.py`."
            pages="2 pages · A4"
          />
          <PrivateBriefRow
            testId="brief-proof-points-brief"
            slug="proof-points-brief"
            title="DARA — Reference & Proof Points (1-Page Tier-1 Eval Pack)"
            sub="Single-page A4 PDF for the moment a technical reviewer asks 'what proof points do you have?'. Inventories 4 classes of substance WITHOUT naming customers: (1) WHAT'S LIVE — click-through-able artefacts (industrial-demo, /ventia/demo, regulator-pack-preview, Pulse Wall, DARA Concierge, SAFE pack). (2) CONNECTOR LIBRARY — 15+ industrial protocols live today (OPC UA, OSIsoft PI, Modbus, BACnet, SNMP, MQTT, Tridium, Honeywell EBI, Siemens Desigo, Cisco/Nokia/Ericsson NMS, Genetec VMS, LIMS, BoM AWAP, CSV/SFTP/S3 fallback) + ~1 new protocol every 4-6 weeks. (3) SECTOR DEPTH — 7 sector profiles with bespoke narrative writers (mining, water, transport, telco, defence, hospitals, environmental). (4) OPERATING POSTURE — sovereign AU, tenant-isolated, read-only ingest, AS/NZS 4360 + IEC 62443 + WSAA G2 + IRAP-aligned, OAuth 2.0/OIDC, sovereign-deploy LLM option. Final dark block honestly states no public reference customers yet, but design-partner reference calls available under NDA. Use this whenever a Tier-1 prospect, investor, or strategic reviewer asks for proof points. Re-generate via `python scripts/generate_proof_points_brief.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-wagering-category-brief"
            slug="wagering-category-brief"
            title="DARA Wagering — The Category Brief (1-Page Investor / Strategic Partner)"
            sub="Single-page A4 PDF designed for: strategic investors (wagering-adjacent VCs — Insight Partners gaming, Bain Capital Ventures, Pollen Street, Australian fintech VCs), board members aligning on the wagering pivot, potential strategic partners (Genius Sports, Sportradar), and DARA's own internal team to keep messaging unified. Contains: (1) MARKET SNAPSHOT — A$250B global TAM across AU/UK/US/EU/NZ/CA with operator counts and compliance-spend ranges per geography. (2) THE WEDGE — Senet/ComplyAdvantage/Quantexa/Sumsub are ingredients, DARA is the meal. (3) 4-PILLAR FRAMEWORK — compact AML/CTF · RG · Integrity · Advertising with regulators covered per pillar. (4) WHY NOW — amber-tinted enforcement table (TaB A$67M · Crown A$450M · Entain £17M · Sky Betting £1.17M · UKGC +40% budget · US state-by-state escalation). (5) UNIT ECONOMICS — Lighthouse Pilot $0 · Standard A$180-300k/yr per pillar · Operating Partner A$600k-1.2M/yr all 4. (6) BOOTSTRAP vs SERIES A MATH — 5 customers = profitable, recommends bootstrap to A$1-2M ARR then raise from strength at A$30-50M valuation (3× better than raising now). Use this as the single document you hand to any strategic investor, wagering-adjacent VC, or board member. Re-generate via `python scripts/generate_wagering_category_brief.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-uk-fire-safety-build-vs-buy"
            slug="uk-fire-safety-build-vs-buy"
            title="DARA — Build vs Buy · UK Fire-Safety Intelligence Layer (1-Page · For Zach / Premier)"
            sub="Founder's back-pocket artefact for the Zach Poole · Premier Compliance UK conversation IF Zach raises the 'we could build this in-house' scenario after engaging with daraos.ai/premier-compliance. Does NOT get sent unsolicited — only surfaced when Zach explicitly asks the build question. Contains: (1) 36-month TCO table for a 50-person UK fire-safety operator — £325-375k Yr1 internal vs £22,500 Yr1 DARA · 3-yr cumulative £950k-£1.08M vs £97.5-£122.5k DARA (~9-10x savings). (2) Four hidden costs of internal build — regulator change cadence (47 BSA/BSR updates in 2024), independent evidence chain (in-house tools = circular evidence to BSR), engineer opportunity cost, permanent maintenance tail. (3) Honest 'when build wins' section conceding two scenarios (Tier-1 FM scale + 1,000+ buildings under management, OR proprietary methodology that materially deviates from BS 8214 / BS 9999). (4) Hybrid close — positions DARA as the engine inside a hybrid where Premier owns the customer-facing brand + custom workflows. Re-generate via `python scripts/generate_uk_fire_safety_build_vs_buy.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-au-insurance-build-vs-buy"
            slug="au-insurance-build-vs-buy"
            title="DARA — Build vs Channel · AU Contractor-Liability Insurance (1-Page · Insurer-as-Channel)"
            sub="Companion to the UK fire-safety brief — same skeleton, AU-insurance-channel framing. Recipient: Risk + Underwriting + Product leadership at AU contractor-liability insurers (IAG/CGU, QBE, Suncorp/Vero, Allianz, Vault Insurance, CHU Underwriting). Wedge framing is INSURER-AS-CHANNEL, not insurer-as-customer — insurer licences DARA and offers it to its insured contractor portfolio (builders, fire/electrical/plumbing trades) as a premium-reduction lever (same mechanism as fleet telematics). Contains: (1) THE WEDGE — three-sided win framing (insurer wins loss-ratio + retention + channel revenue, contractor wins premium discount + evidence layer, DARA wins distribution). (2) 36-month TCO — A$1.24-1.42M Yr1 internal build vs A$60k DARA · 3-yr cumulative A$3.7-4.3M vs A$330-460k DARA (~9-11x). (3) Portfolio channel economics — 5,000 contractors × A$5k blended ARPU × 30% margin = A$7.5M/yr channel revenue + A$2.4-4.3M/yr loss-ratio improvement. (4) Honest 'when build wins' (Tier-1 + 40+ engineers + 30,000+ policies, OR proprietary risk model). (5) Hybrid white-labelled channel close. Re-generate via `python scripts/generate_au_insurance_build_vs_buy.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-contractor-renewal-one-pager"
            slug="contractor-renewal-one-pager"
            title="DARA — Contractor Renewal-Pack Insert · Co-Brandable (1-Page · For the Insured Contractor)"
            sub="Second half of the two-step insurer-channel sale — paired with the AU Insurance Build-vs-Channel brief. The Build-vs-Channel brief sells the INSURER on the channel mechanism; this 1-pager is what the insurer ATTACHES to its renewal pack and forwards to its insured contractor portfolio (builders, fire-safety techs, electricians, plumbers, façade contractors). Plain-English copy written for the SMB contractor owner-operator (NOT a compliance officer, NOT a procurement lead — Year-9 schooling level, tradie vocabulary). Soft warm-tint design intentionally NOT Bloomberg-terminal-styled because this lives in an insurer's renewal pack alongside friendly customer-comms. Contains: (1) HERO — 'Cut your liability premium 8-15%. Cover your compliance evidence at the same time.' (2) WHY THIS EXISTS — 3-paragraph plain-English explanation of the insurer's logic. (3) WHAT YOU GET — 4 numbered benefits in builder-English (daily phone brief · auto-evidence pack · premium discount at renewal · regulator-deadline alerts). (4) HOW IT WORKS — 3-step rollout (Install 15 min · Do work normally · Pay less at renewal). (5) THE MATHS — 3-row worked example table covering A$25k / A$50k / A$120k premium tiers showing net savings of A$750-A$9,000/yr in contractor's pocket. (6) HONEST 'is this for me' — 2 YES scenarios + 1 NO scenario (sole-traders under A$10k premium). (7) NAVY CTA strip — 'Tick the Add-DARA box on your renewal pack.' Co-brandable via INSURER_NAME env var: defaults to '[your insurer]' so an insurer's design team drops their brand at distribution time. Re-generate via `python scripts/generate_contractor_renewal_one_pager.py` OR `INSURER_NAME='QBE Australia' python scripts/generate_contractor_renewal_one_pager.py`."
            pages="1 page · A4"
          />
          <PrivateBriefRow
            testId="brief-founder-bible"
            slug="founder-bible"
            title="DARA — The Founder's Bible (Internal Operating Manual · 12 Pages · For Manuel)"
            sub="Comprehensive personal operating manual prepared for the founder to internalise DARA's product, verticals, pricing, projections, and defensive answer-set before high-stakes conversations. 12 pages · ~5,600 words · ~30 min focused read. Organised in 11 parts: (1) What DARA actually is — 30s, 2min, 10min answers. (2) Why 'terminal' — the three reasons the Bloomberg metaphor matters strategically (pricing, moat, positioning). (3) The four verticals — Wagering / Industrial / UK Fire-Safety / AU Insurance Channel — with TAM, named targets, pricing, and honest read on each. (4) Revenue mechanics — three revenue lines, profitable at 5 customers. (5) Projections through 2032 with HONEST probability column. (6) The 12 hardest questions and the SHAPE of the answer for each — covering Claude-wrapper objection, build-in-house objection, no-paying-customers objection, regulator-approval objection. (7) The 5 things that could kill DARA — named out loud (pre-revenue runway, hyperscaler entry, single bad regulator outcome, founder burnout, wagering antibody problem). (8) The IPO path — 15-22% probability of US$1B+ in 5-7 years, 55-65% of any material exit. (9) The pitch hierarchy — 10s / 30s / 2min / 10min / 30min versions. (10) The week-by-week internalisation drill. (11) Closing reminders. SOURCE OF TRUTH is `/app/memory/FOUNDER_BIBLE.md` — edit there, re-run `python scripts/generate_founder_bible.py` to regenerate this PDF. Intentionally NOT Bloomberg-terminal styled — designed for sustained reading at arm's length on paper or laptop screen."
            pages="12 pages · A4"
          />
          <PrivateBriefRow
            testId="brief-exploration-morning-brief"
            slug="exploration-morning-brief"
            title="DARA — Exploration · The Geologist's Morning Brief · Mock w/ Synthesised Anomaly Heatmap (2 Pages · For Dayo's Resources Channel)"
            sub="Phase 25.16aq · paired with the bespoke /dayo ABM page. Built off the 2026-05-31 meeting with Dayo (financial broker · resources sector) who raised the 'DARA-without-SCADA' objection for exploration mining. Mock 1-page brief for a fictional gold/copper explorer (Aurora Resources Ltd · Garnet Hill North tenement · Eastern Goldfields WA · project code AUR-EGF-04). HERO ELEMENT is a synthesised anomaly heatmap rendered natively in ReportLab (no matplotlib dependency) — 20x14 cell target-rating grid with: cool-to-hot 11-stop colour ramp (deep navy → cyan → green → yellow → orange → red → magenta), 12 drill collar markers (4 highlighted gold for >2.5 g/t intercepts with pulse rings), 3 historical workings (X marks), amber-dashed tenement boundary, hot-zone callout box with 'VRC023/024/025 · 2.8-3.2 g/t Au' annotation, north arrow, scale bar (0-2km), gradient legend, and competitor-proximity arrow ('→ COMP · 2km E'). Page 1 layout: heatmap (hero) · 2-col split with overnight assays table (5 holes with material/watch flags) on left + JORC 2012 clause 19 materiality alert (red-strip emphasis) on right. Page 2: competitor watch (Greatland Metals · Capricorn · GSWA) · ranked next-5-drill-holes capital allocation · compliance/runway strip (Native Title via Marlinyu Ghoorlie · work-programme expenditure · cash runway). Honest 'what this is NOT' footer naming Leapfrog / Datamine / Vulcan as the tools DARA does NOT replace. Surfaced publicly via `/api/public/sample-brief/exploration-morning-brief` (no auth needed — downloadable from /dayo) AND privately here. Re-generate via `python scripts/generate_exploration_morning_brief.py`."
            pages="2 pages · A4"
          />
          <PrivateBriefRow
            testId="brief-heatmap-technical-brief"
            slug="heatmap-technical-brief"
            title="DARA — Exploration Heatmap Technical Reference · Personal Study Brief (4 Pages · For Manuel)"
            sub="Phase 25.16ar · personal-study reference capturing the full word-for-word technical answer to two founder questions raised 2026-06-01: (1) What does 'VRC' mean in the mock brief? (VRC = Vertical Reverse Circulation · standard AU drill-hole prefix convention table covering RC / VRC / DD/DDH / AC / RAB). (2) How is the heatmap actually generated in production? — 6-step deterministic pipeline (95% geostatistics, 5% LLM): Step 1 data ingestion (6 sources table: ALS/SGS/Bureau Veritas assays · acQuire/Datashed/Maxwell drill DB · geochem · geophysics · Sentinel-2/ASTER/EnMAP satellite · ASX/GSWA feeds), Step 2 spatial normalisation to GDA2020/MGA Zone 51 at 50m×50m grid via GeoPandas/Rasterio/GDAL/PROJ, Step 3 per-layer 0-10 scoring (percentile rank · IDW · kriging · Gaussian decay · kernel density · hyperspectral band-ratio), Step 4 chief-geologist-set weighting (drill 45% / geochem 20% / geophysics 15% / spectral 10% / historical 5% / competitor 5%), Step 5 weighted-sum synthesis to GeoTIFF raster, Step 6 Claude Sonnet 4.5 writes prose brief (heatmap is math · LLM only writes English · hallucinations blocked at citation layer). Plus: real deployment onboarding timeline (Day 1-15+ · A$25-40k folded into POC fee), plus the 90-second answer to memorise verbatim for any conversation from CFO to chief geo. SOURCE OF TRUTH is `/app/memory/HEATMAP_TECHNICAL_BRIEF.md` — edit there, re-run `python scripts/generate_heatmap_technical_brief.py` to regenerate. Designed for sustained reading (Helvetica 10pt / 14pt leading), NOT Bloomberg-terminal styled."
            pages="4 pages · A4"
          />
          <PrivateBriefRow
            testId="brief-energy-arbitrage-brief"
            slug="energy-arbitrage-brief"
            title="DARA — Energy · The Arbitrage Morning Brief · Mock w/ Arbitrage Stack Chart (2 Pages · For Burditt's Energy Channel)"
            sub="Phase 25.16aw · paired with the bespoke /burditt ABM page. Built off the 2026-06-01 meeting with Burditt Strategic Planning who watched the Ventia industrial-connector demo but it didn't land — his clients are in energy, not industrial services. His sharp business question reframed energy from cost to revenue: 'my client consumes 43 million [kWh] at A$0.22/kWh, how much can he SELL it for?' Mock 2-page brief for a fictional industrial energy consumer (43 GWh/yr × A$0.22/kWh = A$9.46M annual spend, 4.9 MW average load, 2-4 MW flexible). HERO ELEMENT is a native ReportLab horizontal stacked bar chart visualising 5 stackable arbitrage avenues with confidence bands: (01) RERT Emergency Reserve A$50-300k/yr · zero capex · (02) FCAS Ancillary Services A$130-400k/yr · A$30-80k capex · (03) WDRM Wholesale Demand Response A$80-250k/yr · zero capex · (04) LGCs Renewable Certificates A$0-200k/yr if PPA renewable-backed · (05) BTM Generation Solar+Battery A$200-600k/yr · A$2.5-4M capex 5-7yr payback. Amber-tinted summary box shows A$260k-A$1.15M no-capex total + A$460k-A$1.75M with-capex total = 5-26% recovered against A$9.46M energy spend. Page 1 also: today's recommended actions table (curtailment window · FCAS bid · LGC sell · RERT registration) + red-strip NEM Spot Price Risk Alert. Page 2: Market Watch (NEM SPOT / FCAS Raise6Sec / LGC / ACCU) · 12-month opportunity forecast by avenue with capex + time-to-revenue · AEMC/AER + portfolio risk + cash position strip. Honest 'what this is NOT' footer. Surfaced publicly via `/api/public/sample-brief/energy-arbitrage-brief` (no auth needed — downloadable from /burditt) AND privately here. Re-generate via `python scripts/generate_energy_arbitrage_brief.py`."
            pages="2 pages · A4"
          />
          <PrivateBriefRow
            testId="brief-angel-deal-teaser"
            slug="angel-deal-teaser"
            title="DARA OS — Angel Deal Teaser (One-Pager)"
            sub="Hand-tuned one-page intro for the Silicon Valley angel following daraos.ai. Frames a SAFE @ $6-8M cap, $250K-$500K target, 20% discount. Leads with integration-moat positioning (Xero + MYOB + Square + CSV → ~95% AU SMB reach) over generic AI framing. Closes with 'what's your bar to write a check in 4 weeks?' — not a cash ask. Single page, designed to be sent in the body of an email."
            pages="1 page"
          />
          <PrivateBriefRow
            testId="brief-investor-outreach-kit"
            slug="investor-outreach-kit"
            title="DARA OS — Investor Outreach Kit (Full)"
            sub="Open the day Stripe LIVE keys flip + 5 paying customers on board. Cold email + warm follow-up + monthly update + 5-page pitch memo + 8-question objection cheat sheet + 30-day sprint plan + phrases-to-use/avoid + Bloomberg/Palantir defensibility cheat sheet + acquisition probability map + $8M cap triangulation (3-method walk-through). Recommended path: SAFE @ $8M cap for $750K bridge, then priced $2M @ $12M post in 6 months."
            pages="~26 pages"
          />
          <PrivateBriefRow
            testId="brief-rope-access-one-pager"
            slug="rope-access-one-pager"
            title="DARA for Rope Access Training — Prospect One-Pager"
            sub="Send to rope-access / IRATA / SPRAT training providers before the 25-min discovery call. 6-page Bloomberg-styled brief showing how A$18K/yr unlocks A$446K of conservative-case revenue (re-cert reminders + bay utilisation + corporate pipeline forecasting + project-demand intelligence). Includes the Founding Customer offer. Pair with the seeded demo workspace (login: demo-ropeaccess@daraos.ai)."
            pages="~6 pages"
          />
          <PrivateBriefRow
            testId="brief-square-review-kit"
            slug="square-review-kit"
            title="DARA OS — Square Production App Review Kit"
            sub="Paste-ready submission packet for Square's Production app review form. Cuts the 3-hour submission task to ~30 min. Includes app-description copy · per-scope justification (4 scopes) · technical/security answers · 6-screenshot checklist · demo credentials · follow-up Q&A playbook · final pre-submit checklist. Unblocks all future Square self-serve customer connections."
            pages="~10 pages"
          />
        </div>
      </Panel>

      {/* Phase 25.16c — Outreach templates. Cold emails for prospects +
          angel investors, persistent inside the app instead of buried in
          chat history. Edit source in src/data/outreachTemplates.js. */}
      <Panel
        title="// OUTREACH TEMPLATES"
        testId="outreach-templates-panel"
        meta={<span className="font-mono text-[9px] text-zinc-500">copy-paste · personalise per recipient</span>}
      >
        <div className="p-4 space-y-3">
          <div className="text-xs text-zinc-500 leading-relaxed max-w-3xl">
            Cold email templates for prospects + angel investors. Each card
            ships a subject + body with <code className="text-zinc-300">[BRACKETED]</code> placeholders.
            Click <b>Copy email</b> to grab the body, <b>Copy subject</b> for the line,
            then personalise the bracketed parts before sending. Source file:
            <code className="text-zinc-300 ml-1">src/data/outreachTemplates.js</code>
          </div>
          {OUTREACH_TEMPLATES.map(tpl => (
            <OutreachTemplateRow key={tpl.slug} tpl={tpl} />
          ))}
        </div>
      </Panel>

      {/* Phase 25.14a — Demo workspace seeders. After every fresh production
          deploy, the prod DB is empty for demo accounts. This panel re-seeds
          them with one click. */}
      <Panel
        title="// DEMO WORKSPACE SEEDERS"
        testId="demo-seeders-panel"
        meta={<span className="font-mono text-[9px] text-zinc-500">run after every prod deploy</span>}
      >
        <div className="p-4 space-y-3">
          <div className="text-xs text-zinc-500 leading-relaxed max-w-2xl">
            Deploys ship code, not data. After a fresh deploy to production,
            click <b>Re-seed</b> on the demo workspace you need before a
            customer demo. Idempotent — re-running wipes &amp; rebuilds the
            same workspace with current-date transactions.
          </div>
          <DemoSeedRow
            slug="barber"
            title="Barber Shop — Sharp & Co."
            credentials="barber@dara.ai · barber"
            description="Tier=micro · industry=Barber Shop · 14 days of seeded history + today's transactions up to now · 2 voids injected midday for watch-out demo · auto-routes to glance view."
          />
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scheduler */}
        <Panel title="// SCHEDULER" testId="scheduler-panel" meta={<Clock className="w-3.5 h-3.5" />}>
          <div className="p-4">
            {!scheduler ? <Skel/> : (
              <>
                <div className={`font-mono text-[10px] uppercase tracking-widest mb-3 ${scheduler.running ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
                  STATE · {scheduler.running ? "RUNNING" : "STOPPED"}
                </div>
                <div className="divide-y divide-zinc-900">
                  {scheduler.jobs.length === 0 && <div className="font-mono text-[11px] text-zinc-600 p-3">No jobs registered.</div>}
                  {scheduler.jobs.map(j => (
                    <div key={j.id} className="py-3 flex items-start justify-between gap-3" data-testid={`job-${j.id}`}>
                      <div>
                        <div className="font-heading font-bold text-sm">{j.name || j.id}</div>
                        <div className="font-mono text-[10px] text-zinc-500 mt-0.5 truncate max-w-[280px]">{j.trigger}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">NEXT</div>
                        <div className="font-mono text-[11px] text-[#FFB000]">{j.next_run_at ? new Date(j.next_run_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Panel>

        <SquareManualConnectPanel />

        {/* Integrations */}
        <Panel title="// INTEGRATIONS" testId="integrations-status-panel" meta={<Zap className="w-3.5 h-3.5" />}>
          <div className="p-4">
            {!integrations ? <Skel/> : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(integrations.configured).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 p-2 border border-zinc-800" data-testid={`integration-${k}`}>
                      {v ? <CheckCircle2 className="w-3.5 h-3.5 text-[#00E676] flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-zinc-700 flex-shrink-0" />}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 truncate">{k.replace(/_/g, " ")}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-zinc-900">
                  <Stat label="WORKSPACES" value={integrations.tenants.workspaces} />
                  <Stat label="USERS" value={integrations.tenants.users} />
                  <Stat label="SLACK INSTALLS" value={integrations.tenants.slack_installs} />
                </div>
              </>
            )}
          </div>
        </Panel>

        {/* Recent events */}
        <Panel title="// RECENT EVENTS" testId="events-panel" meta={<Mail className="w-3.5 h-3.5" />}>
          <div className="divide-y divide-zinc-900 max-h-[420px] overflow-auto">
            {!events ? <div className="p-4"><Skel/></div> :
              [...(events.inbound_email || []), ...(events.slack || [])]
                .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
                .slice(0, 25)
                .map(e => (
                  <div key={e.id} className="p-3" data-testid={`event-${e.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${e.kind === "inbound_email" ? "border-[#60A5FA]/40 text-[#60A5FA]" : "border-[#FFB000]/40 text-[#FFB000]"}`}>
                        {e.kind === "inbound_email" ? "EMAIL" : (e.command ? `/dara ${e.command}` : "SLACK")}
                      </span>
                      <span className="font-mono text-[10px] text-zinc-500 truncate">{e.sender_email || e.slack_user_id || "unknown"}</span>
                      <span className="font-mono text-[9px] text-zinc-600 ml-auto flex-shrink-0">{fmtRel(e.created_at)}</span>
                    </div>
                    <div className="font-mono text-[11px] text-zinc-300 line-clamp-2">{e.question}</div>
                  </div>
                ))
            }
            {events && (events.inbound_email.length === 0 && events.slack.length === 0) && (
              <div className="p-6 text-center font-mono text-[11px] text-zinc-600">No events yet.</div>
            )}
          </div>
        </Panel>

        {/* Rate limits */}
        <Panel title="// RATE LIMITS · TOP USERS TODAY" testId="rate-limits-panel" meta={<AlertCircle className="w-3.5 h-3.5" />}>
          <div className="divide-y divide-zinc-900 max-h-[420px] overflow-auto">
            {!rateLimits ? <div className="p-4"><Skel/></div> : rateLimits.top_users.length === 0 ? (
              <div className="p-6 text-center font-mono text-[11px] text-zinc-600">No usage today.</div>
            ) : rateLimits.top_users.map(u => (
              <div key={`${u.user_id}-${u.action}`} className="p-3 flex items-center justify-between gap-3" data-testid={`ratelimit-${u.user_id}-${u.action}`}>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-xs truncate">{u.user_name || u.user_email}</div>
                  <div className="font-mono text-[9px] text-zinc-500 truncate">{u.user_email}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{u.action}</span>
                  <span className="font-mono text-base text-[#FFB000]">{u.used_today}</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        {/* Briefing health */}
        <Panel title={`// BRIEFINGS · LAST 7 DAYS`} testId="briefings-health-panel" meta={<Briefcase className="w-3.5 h-3.5" />} className="lg:col-span-2">
          <div className="divide-y divide-zinc-900 max-h-[300px] overflow-auto">
            {!briefings ? <div className="p-4"><Skel/></div> : briefings.workspaces.length === 0 ? (
              <div className="p-6 text-center font-mono text-[11px] text-zinc-600">No briefings generated in this window.</div>
            ) : briefings.workspaces.map(w => (
              <div key={w.workspace_id} className="p-3 flex items-center justify-between gap-3" data-testid={`briefing-ws-${w.workspace_id}`}>
                <div className="min-w-0 flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5 text-zinc-700 flex-shrink-0" />
                  <span className="font-mono text-[11px] text-zinc-300 truncate">{w.workspace_id}</span>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0 font-mono text-[10px]">
                  <span className="text-[#FFB000]">{w.briefings} runs</span>
                  <span className="text-zinc-500">{fmtRel(w.last_at)}</span>
                  <span className="text-[#00E676]">${(w.latest_pipeline / 1e6).toFixed(1)}M</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="text-center pt-4">
        <Link to="/app/admin/calls" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000]">
          ← Sales inbox
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-2xl font-medium text-white mt-1">{value}</div>
    </div>
  );
}

function Skel() {
  return <div className="font-mono text-[11px] text-zinc-600 animate-pulse">LOADING…</div>;
}

// Phase 25.11 — Private briefs row. Uses authenticated fetch + blob download
// so the bearer token is never exposed in a URL.
function PrivateBriefRow({ testId, slug, title, sub, pages }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/internal/brief/${slug}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dara-${slug}-brief.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-3 border border-zinc-800 bg-zinc-950/40 p-3 sm:p-4"
    >
      <span className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
        <FileText className="w-4 h-4 text-[#FFB000]" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm sm:text-base text-white leading-tight">{title}</div>
        <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">{sub}</div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1.5">{pages} · PDF · CONFIDENTIAL</div>
      </div>
      <button
        onClick={download}
        disabled={busy}
        data-testid={`${testId}-download`}
        className="flex-shrink-0 bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        <Download className="w-3 h-3" />
        {busy ? "…" : "DOWNLOAD"}
      </button>
    </div>
  );
}

// Phase 25.14a — DemoSeedRow. One-click "re-seed this demo workspace" button
// for use after every fresh production deploy.
// Phase 25.16c — OutreachTemplateRow. Renders one cold-email template as
// a collapsible card with copy-to-clipboard for body + subject.
function OutreachTemplateRow({ tpl }) {
  const [expanded, setExpanded] = useState(false);

  const doCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy — try selecting manually");
    }
  };

  return (
    <div
      data-testid={`outreach-template-${tpl.slug}`}
      className="border border-zinc-800 bg-zinc-950/40"
    >
      <div className="p-3 sm:p-4 flex items-start gap-3 flex-wrap">
        <span className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
          <Mail className="w-4 h-4 text-[#FFB000]" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
            {tpl.category}
          </div>
          <div className="font-heading font-bold text-sm sm:text-base text-white leading-tight mt-0.5">
            {tpl.title}
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">{tpl.use_case}</div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
          <button
            onClick={() => doCopy(tpl.subject, "Subject")}
            data-testid={`outreach-${tpl.slug}-copy-subject`}
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-700 px-2 py-1.5 inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> Subject
          </button>
          <button
            onClick={() => doCopy(tpl.body, "Email body")}
            data-testid={`outreach-${tpl.slug}-copy-body`}
            className="font-mono text-[10px] uppercase tracking-widest text-black bg-[#FFB000] hover:opacity-90 px-2.5 py-1.5 font-bold inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> Copy Email
          </button>
          <button
            onClick={() => setExpanded(v => !v)}
            data-testid={`outreach-${tpl.slug}-toggle`}
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white px-2 py-1.5"
          >
            {expanded ? "Hide" : "Preview"}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-zinc-900 p-3 sm:p-4 space-y-3 bg-black/40">
          {tpl.research_hint && (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                // RESEARCH HINT
              </div>
              <div className="font-mono text-[11px] text-zinc-300 leading-relaxed">{tpl.research_hint}</div>
            </div>
          )}
          <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
              // SUBJECT
            </div>
            <div className="font-mono text-[12px] text-white">{tpl.subject}</div>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
              // BODY
            </div>
            <pre className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap break-words">
{tpl.body}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}



function DemoSeedRow({ slug, title, credentials, description }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post(`/admin/seed-demo/${slug}`);
      setResult({ ok: true, msg: res.data?.message || "Seeded." });
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || "Seed failed" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid={`demo-seed-${slug}`}
      className="flex items-start gap-3 border border-zinc-800 bg-zinc-950/40 p-3 sm:p-4"
    >
      <span className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
        <Sparkles className="w-4 h-4 text-[#FFB000]" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm sm:text-base text-white leading-tight">{title}</div>
        <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">{description}</div>
        <div className="font-mono text-[10px] text-[#FFB000] mt-1.5">
          login · {credentials}
        </div>
        {result && (
          <div
            data-testid={`demo-seed-${slug}-result`}
            className={`font-mono text-[10px] mt-2 ${result.ok ? "text-[#00E676]" : "text-[#FF3B30]"}`}
          >
            {result.msg}
          </div>
        )}
      </div>
      <button
        onClick={run}
        disabled={busy}
        data-testid={`demo-seed-${slug}-btn`}
        className="flex-shrink-0 bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        {busy ? "Seeding…" : "Re-seed"}
      </button>
    </div>
  );
}


// Phase 25.13h — Square Manual Connect panel.
// Pastes a Personal Access Token directly into the workspace's Square integration,
// bypassing the OAuth flow. Useful for:
//   · Demos before Square's production app review is approved
//   · Founder's own account during testing
//   · Migrating from sandbox-only credentials
function SquareManualConnectPanel() {
  const [token, setToken] = useState("");
  const [env, setEnv] = useState("production");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!token || token.trim().length < 10) {
      toast.error("Paste a Square access token (≥ 10 chars)");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/pos/square/connect-manual", {
        access_token: token.trim(),
        environment: env,
      });
      setResult({ ok: true, data: res.data });
      setToken("");
      toast.success(`Connected: ${res.data.business_name || res.data.merchant_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Connect failed";
      setResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel
      label="// SQUARE · MANUAL CONNECT"
      eyebrow="OAUTH BYPASS · SUPER-ADMIN"
      data-testid="square-manual-connect-panel"
    >
      <div className="p-5 space-y-4">
        <div className="font-mono text-[11px] text-zinc-400 leading-relaxed">
          Paste a Square <strong className="text-white">Personal Access Token</strong> below to
          bypass the OAuth flow. Use this when Square's production app review is pending or to
          connect your own Square account for demos. The token is encrypted and stored
          per-workspace.
        </div>
        <div className="font-mono text-[10px] text-zinc-500 leading-relaxed border border-zinc-900 bg-zinc-950/50 p-3">
          ▸ Get your token at <span className="text-[#FFB000]">developer.squareup.com/apps</span> → your DARA app → <strong>Production</strong> tab → <strong>OAuth</strong> → <strong>Personal Access Token</strong>.<br />
          ▸ Required scopes: MERCHANT_PROFILE_READ, PAYMENTS_READ, ORDERS_READ, ITEMS_READ.<br />
          ▸ Token does not expire (until you rotate it in the Square dashboard).
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">
              Square Access Token
            </label>
            <input
              type="password"
              data-testid="square-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="EAAA..."
              className="w-full bg-zinc-950 border border-zinc-800 text-white font-mono text-[12px] px-3 py-2 focus:outline-none focus:border-[#FFB000]"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">
              Environment
            </label>
            <select
              data-testid="square-env-select"
              value={env}
              onChange={(e) => setEnv(e.target.value)}
              className="bg-zinc-950 border border-zinc-800 text-white font-mono text-[12px] px-3 py-2 focus:outline-none focus:border-[#FFB000]"
            >
              <option value="production">PRODUCTION (real merchants)</option>
              <option value="sandbox">SANDBOX (test sellers only)</option>
            </select>
          </div>
          <button
            onClick={submit}
            disabled={busy || !token}
            data-testid="square-manual-connect-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2 w-fit"
          >
            <Plug className="w-3.5 h-3.5" />
            {busy ? "Validating…" : "Connect Square Account"}
          </button>
        </div>
        {result?.ok && (
          <div className="border border-[#00E676]/40 bg-[#00E676]/5 p-4 font-mono text-[11px] text-zinc-200 space-y-1" data-testid="square-connect-result-ok">
            <div className="flex items-center gap-2 text-[#00E676] font-bold">
              <CheckCircle2 className="w-4 h-4" /> CONNECTED
            </div>
            <div>Business: <strong>{result.data.business_name || "—"}</strong></div>
            <div>Location: <strong>{result.data.location_name || "—"}</strong></div>
            <div>Currency: <strong>{result.data.currency}</strong></div>
            <div>Merchant ID: <strong>{result.data.merchant_id}</strong></div>
            <div className="text-zinc-500 mt-2">30-day transaction backfill running in background. Refresh /pos in a few minutes.</div>
          </div>
        )}
        {result && !result.ok && (
          <div className="border border-red-500/40 bg-red-500/5 p-4 font-mono text-[11px] text-red-300" data-testid="square-connect-result-error">
            <div className="flex items-center gap-2 font-bold mb-1">
              <XCircle className="w-4 h-4" /> CONNECT FAILED
            </div>
            <div className="text-zinc-300">{result.error}</div>
          </div>
        )}
      </div>
    </Panel>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Bespoke Proposals Panel — single source of truth for every 1-to-1
// outreach landing page DARA has built. Pages are deliberately unlinked
// from public nav (direct-URL only). This panel exists so the founder
// stops having to memorise 5+ slug names.
// ─────────────────────────────────────────────────────────────────────
const BESPOKE_PROPOSALS = [
  {
    id: "burditt",
    prepared_for: "Burditt Krost · Strategic Planning Institute P/L (Perth WA)",
    title: "DARA × Strategic Planning Institute · Founding Partner",
    pitch: "Founding-partner channel deal · 30 % recurring + 50 % Y1 anchor (first 3 clients) · capped at 5 globally.",
    path: "/strategic-planning-institute",
    aliases: ["/burditt"],
    pdf: "/api/public/partner-program-brief",
    lead_industry: "strategic-planning-institute",
    status: "active",
    note: "Meeting next week. Do NOT send URL or PDF before in-person conversation. Walk him through on iPad, hand him printed PDF afterwards.",
  },
  {
    id: "iwg",
    prepared_for: "Mercedes Bankston · IWG (Tech / Partnership / Venture Acceleration)",
    title: "DARA × IWG · 175,000-SMB Tenant Network",
    pitch: "Distribution channel into IWG / Spaces / Regus tenant base · 3 deal models · 90-day pilot framing.",
    path: "/iwg",
    aliases: [],
    pdf: "/api/public/iwg-tenant-brief",
    lead_industry: "iwg",
    status: "active",
    note: "Hold in reserve. Mercedes returns with named property-owner intros first.",
  },
  {
    id: "ventia",
    prepared_for: "Ventia (infrastructure services major · 35k FTE · 400+ sites AU/NZ)",
    title: "DARA × Ventia · 7-Sector Mapping",
    pitch: "Enterprise proposal across 7 service lines · 3 deal models · ABM-grade framing.",
    path: "/ventia",
    aliases: [],
    pdf: null,
    lead_industry: "ventia",
    status: "active",
    note: "Active prospect Feb 2026. Watchlist-tracked.",
  },
  {
    id: "ai-maturity-benchmark",
    prepared_for: "AU mid-tier ADI banking executives (CEO-in-transition outreach)",
    title: "AU Mid-Tier ADI · AI Maturity Benchmark",
    pitch: "External benchmark scoring 6 AU mid-tier ADIs across 5 AI dimensions · inbound-friendly lead magnet.",
    path: "/ai-maturity-benchmark",
    aliases: [],
    pdf: "/api/public/ai-maturity-benchmark",
    lead_industry: "ai-maturity-benchmark",
    status: "active",
    note: "Public benchmark · safe to share via DM to banking executives.",
  },
  {
    id: "industrial-demo",
    prepared_for: "Mining superintendents · investors · industrial-vertical prospects",
    title: "Industrial Connector · Live Demo",
    pitch: "Standalone screenshare-ready artefact. 7 simulated tags streaming live, anomaly grading, auto-brief, architecture flow. Drop into Loom or Zoom.",
    path: "/industrial-demo",
    aliases: ["/connector-demo"],
    pdf: "/api/public/industrial-connector-brief",
    lead_industry: "industrial-connector",
    status: "active",
    note: "Show this on Burditt's iPad after the meeting. Show it to any tier-2 mining superintendent. No form, no CTA pressure — just the engine running.",
  },
];

function BespokeProposalsPanel() {
  const [counts, setCounts] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    setOrigin(window.location.origin);
    api.get("/admin/leads-unified")
      .then(r => {
        const c = {};
        for (const lead of r.data.industry_leads || []) {
          c[lead.industry] = (c[lead.industry] || 0) + 1;
        }
        setCounts(c);
      })
      .catch(() => { /* swallow — counts are nice-to-have */ });
    api.get("/admin/bespoke-proposals/analytics?days=14")
      .then(r => {
        const a = {};
        for (const p of r.data.proposals || []) {
          a[p.id] = p;
        }
        setAnalytics(a);
      })
      .catch(() => { /* swallow — page count is optional */ });
  }, []);

  const copy = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select manually");
    }
  };

  return (
    <Panel
      title="// BESPOKE PROPOSALS · 1-TO-1 OUTREACH PAGES"
      testId="bespoke-proposals-panel"
      meta={<Link2 className="w-3.5 h-3.5 text-[#FFB000]" />}
    >
      <div className="p-4 sm:p-5 space-y-3">
        <p className="text-xs text-zinc-400 leading-relaxed">
          Direct-URL bespoke landing pages prepared for named individuals. <span className="text-zinc-500">Never linked from public nav, never indexed.</span> This is your index — copy a URL into an email, or open the page directly. Lead counts show how many people have submitted the engage-form on each page.
        </p>
        {BESPOKE_PROPOSALS.map((p) => {
          const fullUrl = `${origin || "https://daraos.ai"}${p.path}`;
          const pdfUrl = p.pdf ? `${origin || "https://daraos.ai"}${p.pdf}` : null;
          const leadCount = counts[p.lead_industry] || 0;
          const stats = analytics[p.id];
          return (
            <div
              key={p.id}
              className="border border-zinc-800 bg-zinc-950/40 p-4"
              data-testid={`bespoke-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">
                    PREPARED FOR · {p.prepared_for}
                  </div>
                  <h3 className="font-heading font-bold text-base tracking-tight">{p.title}</h3>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5">
                    {leadCount} {leadCount === 1 ? "lead" : "leads"}
                  </span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${
                    p.status === "active"
                      ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5"
                      : "border-zinc-700 text-zinc-500 bg-zinc-900"
                  }`}>
                    {p.status}
                  </span>
                </div>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed mb-3">{p.pitch}</p>

              {/* Analytics strip — pageviews (14-day window), sparkline, last visitor */}
              {stats && (
                <div className="border border-zinc-900 bg-black p-3 mb-3" data-testid={`bespoke-${p.id}-analytics`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <BespokeStat label="VIEWS · 14d" value={stats.total_views} accent={stats.total_views > 0} />
                    <BespokeStat label="UNIQUE" value={stats.unique_visitors} />
                    {stats.linkedin_views > 0 && <BespokeStat label="LINKEDIN" value={stats.linkedin_views} accent />}
                    <Sparkline points={stats.sparkline} />
                    {stats.last_view && (
                      <div className="ml-auto text-right min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-0.5">LAST VIEW</div>
                        <div className="font-mono text-[10px] text-zinc-300 truncate max-w-[260px]">
                          {stats.last_view.org || stats.last_view.referrer_host || "—"}
                          {stats.last_view.country && <span className="text-zinc-500"> · {stats.last_view.country}</span>}
                          {stats.last_view.from_linkedin && <span className="text-[#FFB000]"> · LI</span>}
                        </div>
                        <div className="font-mono text-[9px] text-zinc-500">{fmtRelative(stats.last_view.at)}</div>
                      </div>
                    )}
                    {!stats.last_view && (
                      <div className="ml-auto font-mono text-[10px] text-zinc-600">— no views in window —</div>
                    )}
                  </div>
                </div>
              )}

              {/* Primary URL */}
              <div className="flex items-center gap-2 mb-2">
                <code className="flex-1 font-mono text-[11px] text-[#FFB000] bg-black border border-zinc-800 px-3 py-2 truncate" data-testid={`bespoke-${p.id}-url`}>
                  {fullUrl}
                </code>
                <button
                  type="button"
                  onClick={() => copy(fullUrl, "Page URL")}
                  data-testid={`bespoke-${p.id}-copy`}
                  className="font-mono text-[10px] uppercase tracking-widest border border-zinc-700 hover:border-[#FFB000] hover:text-[#FFB000] px-3 py-2 flex items-center gap-1.5 flex-shrink-0"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <a
                  href={p.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`bespoke-${p.id}-open`}
                  className="font-mono text-[10px] uppercase tracking-widest border border-zinc-700 hover:border-[#FFB000] hover:text-[#FFB000] px-3 py-2 flex items-center gap-1.5 flex-shrink-0"
                >
                  <ExternalLink className="w-3 h-3" /> Open
                </a>
              </div>

              {/* Aliases */}
              {p.aliases.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">ALIASES</span>
                  {p.aliases.map((a) => {
                    const aliasUrl = `${origin || "https://daraos.ai"}${a}`;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => copy(aliasUrl, `Alias ${a}`)}
                        data-testid={`bespoke-${p.id}-alias-${a.replace(/\//g, "")}`}
                        className="font-mono text-[10px] text-zinc-400 hover:text-[#FFB000] underline-offset-4 hover:underline"
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* PDF */}
              {pdfUrl && (
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">PDF</span>
                  <a
                    href={p.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`bespoke-${p.id}-pdf`}
                    className="font-mono text-[10px] text-zinc-400 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" /> {p.pdf.split("/").pop()}
                  </a>
                  <button
                    type="button"
                    onClick={() => copy(pdfUrl, "PDF URL")}
                    data-testid={`bespoke-${p.id}-pdf-copy`}
                    className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000]"
                  >
                    Copy URL
                  </button>
                </div>
              )}

              {/* Operator note */}
              {p.note && (
                <p className="font-mono text-[10px] text-zinc-500 leading-relaxed mt-2 border-l-2 border-zinc-800 pl-3">
                  {p.note}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Tiny presentation helpers for the bespoke-proposals analytics strip.
// ─────────────────────────────────────────────────────────────────────
function BespokeStat({ label, value, accent }) {
  return (
    <div className="min-w-[60px]">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-0.5">{label}</div>
      <div className={`font-heading font-bold text-base tracking-tight ${accent ? "text-[#FFB000]" : "text-zinc-200"}`}>{value}</div>
    </div>
  );
}

function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  const W = 84;
  const H = 28;
  const stepX = W / Math.max(1, points.length - 1);
  const pathD = points
    .map((p, i) => {
      const x = i * stepX;
      const y = H - (p.count / max) * (H - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const hasData = points.some((p) => p.count > 0);
  return (
    <div className="min-w-[100px]">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-0.5">14-DAY TREND</div>
      <svg width={W} height={H} className="block">
        {hasData ? (
          <>
            <path d={pathD} stroke="#FFB000" strokeWidth="1.5" fill="none" />
            {points.map((p, i) => p.count > 0 && (
              <circle
                key={p.day}
                cx={i * stepX}
                cy={H - (p.count / max) * (H - 4) - 2}
                r="1.5"
                fill="#FFB000"
              />
            ))}
          </>
        ) : (
          <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="#3f3f46" strokeWidth="1" strokeDasharray="2 2" />
        )}
      </svg>
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    const m = Math.round(diffMs / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch {
    return "—";
  }
}

// Phase 25.16r — Homepage signal-strip editor.
// 4 integer counters + 1 label band, persisted to db.homepage_ticker.
function HomepageTickerPanel() {
  const [data, setData] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/ticker").then(r => {
      setData(r.data);
      setDraft(r.data);
    }).catch(() => setData({ verticals: 0, prospects: 0, named_targets: 0, pilots_live: 0, label: "" }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        verticals: Number(draft.verticals),
        prospects: Number(draft.prospects),
        named_targets: Number(draft.named_targets),
        pilots_live: Number(draft.pilots_live),
        label: String(draft.label || "").slice(0, 80),
      };
      const r = await api.put("/admin/ticker", payload);
      setData(r.data);
      setDraft(r.data);
      toast.success("Homepage signal strip updated · live on daraos.ai");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  // Phase 25.16s — One-click approval that promotes pending_prospects
  // (auto-incremented by bespoke/category page submissions) into the
  // public PROSPECTS counter. Single click pushes live to daraos.ai.
  const approvePending = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/ticker/approve-pending");
      const { approved, ...rest } = r.data;
      setData(rest);
      setDraft(rest);
      if (approved > 0) {
        toast.success(`Approved ${approved} pending · pushed live`);
      } else {
        toast.info("Nothing pending to approve");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const dirty = JSON.stringify(draft) !== JSON.stringify(data);

  const NumberField = ({ k, label, hint }) => (
    <label className="block" data-testid={`ticker-field-${k}`}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type="number"
        min={0}
        max={k === "pilots_live" ? 99 : 999}
        value={draft[k] ?? 0}
        onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-base text-white focus:outline-none focus:border-[#FFB000]"
      />
      <span className="text-[10px] text-zinc-600 mt-1 block">{hint}</span>
    </label>
  );

  return (
    <Panel title="// HOMEPAGE SIGNAL STRIP · LIVE ON daraos.ai" testId="homepage-ticker-panel">
      <div className="p-4 sm:p-5 space-y-4">
        <p className="text-xs text-zinc-400 leading-relaxed">
          Founder-editable signal counts displayed in the daraos.ai homepage hero strip. Numbers refresh on the next page load.
          NO operator names are exposed — only counts. Use it to signal velocity to inbound visitors without revealing the playbook.
        </p>
        {/* Phase 25.16s — Pending-approval banner. Surfaces auto-incremented
            pending_prospects from bespoke/category page submissions. */}
        {(data.pending_prospects ?? 0) > 0 && (
          <div
            className="border border-[#00E676]/40 bg-[#00E676]/5 p-4 flex items-center justify-between flex-wrap gap-3"
            data-testid="ticker-pending-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full bg-[#00E676] animate-pulse flex-shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">
                  // {data.pending_prospects} NEW PROSPECT{data.pending_prospects === 1 ? "" : "S"} · AWAITING APPROVAL
                </div>
                <div className="text-xs text-zinc-300 mt-0.5">
                  Bespoke / category page submissions. Approve to push live into the public PROSPECTS counter.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={approvePending}
              disabled={saving}
              data-testid="ticker-approve-pending"
              className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-[#00E676] text-black hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
            >
              {saving ? "Working..." : `Approve ${data.pending_prospects} · Push live`}
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <NumberField k="verticals" label="VERTICALS" hint="Number of active product lines (2 today)" />
          <NumberField k="prospects" label="PROSPECTS · ACTIVE" hint="Conversations in progress" />
          <NumberField k="named_targets" label="NAMED TARGETS" hint="ICP list size in pipeline" />
          <NumberField k="pilots_live" label="PILOTS · LIVE" hint="Increment when first paid pilot signs" />
        </div>
        <label className="block" data-testid="ticker-field-label">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">LABEL BAND · TOP-LEFT TEXT</span>
          <input
            type="text"
            maxLength={80}
            value={draft.label ?? ""}
            onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
            placeholder="ACTIVE SIGNAL · UPDATED LIVE"
            className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-[#FFB000] focus:outline-none focus:border-[#FFB000]"
          />
          <span className="text-[10px] text-zinc-600 mt-1 block">
            Edit per campaign cycle. Examples: &quot;ACTIVE SIGNAL · UPDATED LIVE&quot;,
            &quot;SHIPPING WEEK · 12 ARTEFACTS LIVE&quot;,
            &quot;DESIGN PARTNER ENGAGED&quot;.
          </span>
        </label>
        <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-zinc-900">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            // Last updated {fmtRelative(data.updated_at)} by {data.updated_by || "—"}
          </div>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="ticker-save-btn"
            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-[#FFB000] text-black hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? "Saving..." : dirty ? "Save · Push live to daraos.ai" : "No changes"}
          </button>
        </div>
      </div>
    </Panel>
  );
}


// Phase 25.16x — Weekly leads funnel tile.
// Single-glance pulse of inbound activity across the 3 lead pipelines
// (wagering · industrial · bespoke). Click any bucket to drill into
// the AdminLeads page filtered to that pipeline. Auto-refreshes every
// 60s so the founder can watch the funnel breathe.
function WeeklyLeadsFunnelTile() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(7);
  const [refreshing, setRefreshing] = React.useState(false);

  const load = React.useCallback(async (d) => {
    setRefreshing(true);
    try {
      const r = await api.get(`/admin/industry-leads/funnel?days=${d}`);
      setData(r.data);
    } catch (_) { /* swallow */ }
    setRefreshing(false);
  }, []);

  React.useEffect(() => {
    load(days);
    const t = setInterval(() => load(days), 60_000);
    return () => clearInterval(t);
  }, [days, load]);

  const total = data?.total ?? 0;
  const new24h = data?.new_24h_total ?? 0;
  const buckets = data?.buckets ?? [];
  const otherCount = data?.other?.count ?? 0;

  return (
    <Panel
      title={`// WEEKLY LEADS FUNNEL · LAST ${days} DAYS`}
      testId="weekly-funnel-panel"
      meta={<TrendingUp className="w-3.5 h-3.5 text-[#FFB000]" />}
    >
      <div className="p-4 sm:p-5 space-y-4">
        {/* Header strip — total + 24h pulse + window selector */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-baseline gap-4">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">TOTAL · {days}D</div>
              <div className="font-heading font-black text-3xl tracking-tight text-white" data-testid="funnel-total">
                {total}
              </div>
            </div>
            <div className="border-l border-zinc-800 pl-4">
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">NEW · 24H</div>
              <div className={`font-heading font-black text-2xl tracking-tight ${new24h > 0 ? "text-[#00E676]" : "text-zinc-600"}`} data-testid="funnel-new-24h">
                {new24h > 0 ? `+${new24h}` : "0"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`funnel-window-${d}d`}
                className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border ${
                  days === d
                    ? "bg-[#FFB000] text-black border-[#FFB000]"
                    : "border-zinc-800 text-zinc-400 hover:text-[#FFB000] hover:border-[#FFB000]/50"
                }`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={() => load(days)}
              disabled={refreshing}
              data-testid="funnel-refresh-btn"
              className="border border-zinc-800 px-2.5 py-1 text-zinc-400 hover:text-[#FFB000] hover:border-[#FFB000]/50 disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* 4 bucket cards · Phase 25.16ax added POS LIVE · SMB */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="funnel-buckets">
          {buckets.map((b) => (
            <FunnelBucket key={b.key} bucket={b} />
          ))}
        </div>

        {/* Other / unbucketed (only shows if non-zero) */}
        {otherCount > 0 && (
          <div className="border-t border-zinc-900 pt-3 flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              · OTHER / UNBUCKETED INDUSTRIES
            </div>
            <div className="font-mono text-xs text-zinc-400" data-testid="funnel-other-count">
              {otherCount} leads
            </div>
          </div>
        )}

        {/* Click-through */}
        <div className="border-t border-zinc-900 pt-3 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 leading-relaxed">
            // {data?.since ? `since ${new Date(data.since).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}` : "rolling window"} · auto-refresh 60s
          </p>
          <Link
            to="/app/admin/leads"
            data-testid="funnel-view-all-link"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#FFB000] hover:opacity-80"
          >
            View all leads <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </Panel>
  );
}

function FunnelBucket({ bucket }) {
  const { key, label, count, new_24h: new24h, industries, latest } = bucket;
  const hot = new24h > 0;
  return (
    <Link
      to="/app/admin/leads"
      data-testid={`funnel-bucket-${key}`}
      className={`block border p-4 transition-colors ${
        hot
          ? "border-[#FFB000]/40 bg-[#FFB000]/5 hover:bg-[#FFB000]/10"
          : "border-zinc-900 bg-black hover:border-zinc-700"
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
          // {label}
        </div>
        {hot && (
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
            +{new24h} · 24h
          </div>
        )}
      </div>
      <div className="font-heading font-black text-4xl tracking-tight text-white">
        {count}
      </div>
      {latest && (
        <div className="mt-2 font-mono text-[10px] leading-relaxed truncate" data-testid={`funnel-latest-${key}`}>
          <span className="text-zinc-500">LATEST · </span>
          <span className="text-white font-bold">{latest.label}</span>
          <span className="text-zinc-500"> · {fmtRel(latest.created_at)}</span>
        </div>
      )}
      <div className="font-mono text-[10px] text-zinc-600 mt-1.5 leading-relaxed truncate" title={industries.join(", ")}>
        {industries.join(" · ")}
      </div>
    </Link>
  );
}

// ─── Exploration Trial · 7-day signals tile (Phase 25.16ay-deepen) ────────
// Surfaces volume + materiality-flag ratio + recent tenement names + UTC
// time-of-day heatmap from db.exploration_trial_briefs. Honours the
// privacy promise — the underlying DB doesn't store raw Au g/t numbers,
// so this tile cannot leak them.
function ExplorationTrialSignalsTile() {
  const [data, setData] = React.useState(null);
  const [days, setDays] = React.useState(7);
  const [refreshing, setRefreshing] = React.useState(false);

  const load = React.useCallback(async (d) => {
    setRefreshing(true);
    try {
      const r = await api.get(`/admin/exploration-trial/signals?days=${d}`);
      setData(r.data);
    } catch (_) { /* swallow */ }
    setRefreshing(false);
  }, []);

  React.useEffect(() => {
    load(days);
    const t = setInterval(() => load(days), 60_000);
    return () => clearInterval(t);
  }, [days, load]);

  const total = data?.total_briefs ?? 0;
  const last24h = data?.last_24h ?? 0;
  const matRatio = data?.materiality_ratio_pct ?? 0;
  const avgHoles = data?.avg_holes_per_brief ?? 0;
  const hourBuckets = data?.hour_buckets_utc ?? Array(24).fill(0);
  const recent = data?.recent_tenements ?? [];
  const maxHour = Math.max(1, ...hourBuckets);

  return (
    <Panel
      title="// EXPLORATION TRIAL · LAST 7 DAYS · SELF-SERVE SIGNALS"
      testId="exploration-trial-signals-panel"
      meta={(
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-zinc-950 border border-zinc-800 text-zinc-300 font-mono text-[10px] uppercase px-2 py-1"
            data-testid="exploration-trial-days-select"
          >
            <option value={1}>24H</option>
            <option value={7}>7D</option>
            <option value={14}>14D</option>
            <option value={30}>30D</option>
          </select>
          {refreshing && <Loader2 className="w-3 h-3 animate-spin text-[#FFB000]" />}
        </div>
      )}
    >
      <div className="p-4 sm:p-5 space-y-4">
        {total === 0 ? (
          <div className="text-xs text-zinc-500 italic py-6 text-center" data-testid="exploration-trial-empty">
            No trial brief generations in the last {days === 1 ? "24 hours" : `${days} days`}.
            Send <span className="font-mono text-[#FFB000]">daraos.ai/exploration-trial</span> to a prospect and they'll show up here.
          </div>
        ) : (
          <>
            {/* Stat tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900" data-testid="exploration-trial-stats">
              <ETStatTile label="Briefs" value={total} tone="white" />
              <ETStatTile label="Last 24h" value={last24h} tone={last24h > 0 ? "amber" : "muted"} />
              <ETStatTile label="Material" value={`${matRatio}%`} tone={matRatio >= 50 ? "amber" : "white"} sub={`${data?.material_count || 0}/${total} flagged JORC 19`} />
              <ETStatTile label="Avg holes" value={avgHoles} tone="white" />
            </div>

            {/* Recent tenements list */}
            <div data-testid="exploration-trial-recent">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// RECENT TENEMENTS · MOST-RECENT FIRST</div>
              <div className="border border-zinc-900 divide-y divide-zinc-900">
                {recent.map((r, i) => (
                  <div key={i} className="px-3 py-2 flex items-center justify-between gap-3 hover:bg-zinc-950/50 flex-wrap" data-testid={`exploration-trial-tenement-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-zinc-200 truncate">
                        {r.project_name} <span className="text-zinc-600">·</span> <span className="text-zinc-400">{r.tenement_id}</span>
                      </div>
                      <div className="font-mono text-[10px] text-zinc-500 truncate">
                        {r.jurisdiction} · {r.commodity} · {r.hole_count} hole{r.hole_count === 1 ? "" : "s"} · {formatRelative(r.created_at)}
                      </div>
                    </div>
                    {r.materiality_flag && (
                      <span className="font-mono text-[9px] uppercase px-2 py-0.5 bg-red-900/30 text-red-300 border border-red-800 whitespace-nowrap">
                        Material
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Hour-of-day heatmap */}
            <div data-testid="exploration-trial-hourmap">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                // BRIEFS BY HOUR OF DAY · UTC · DARKER = MORE
              </div>
              <div className="grid grid-cols-24 gap-px border border-zinc-900" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                {hourBuckets.map((count, h) => {
                  const intensity = count / maxHour;
                  return (
                    <div
                      key={h}
                      className="h-8 flex items-center justify-center font-mono text-[8px]"
                      style={{
                        backgroundColor: count === 0 ? "#0a0a0a" : `rgba(255, 176, 0, ${0.15 + intensity * 0.85})`,
                        color: intensity > 0.5 ? "#000" : "#666",
                      }}
                      title={`${h.toString().padStart(2, "0")}:00 UTC · ${count} brief${count === 1 ? "" : "s"}`}
                    >
                      {count > 0 ? count : ""}
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-24 gap-px text-center font-mono text-[8px] text-zinc-600 mt-1" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                {Array.from({ length: 24 }).map((_, h) => (
                  <div key={h}>{h % 4 === 0 ? `${h.toString().padStart(2, "0")}` : ""}</div>
                ))}
              </div>
            </div>
          </>
        )}

        <p className="text-[10px] text-zinc-600 leading-relaxed font-mono pt-2 border-t border-zinc-900">
          // privacy posture: raw assay numbers are NEVER persisted server-side.
          this tile shows tenement metadata + materiality flag only.
        </p>
      </div>
    </Panel>
  );
}

function ETStatTile({ label, value, tone, sub }) {
  const valueColor = tone === "amber" ? "text-[#FFB000]" : tone === "muted" ? "text-zinc-500" : "text-white";
  return (
    <div className="bg-black p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-xl font-bold mt-1 ${valueColor}`}>{value}</div>
      {sub && <div className="font-mono text-[9px] text-zinc-600 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "—";
  }
}

